"use client"

import { useEffect } from "react"

interface UseTabAttentionArgs {
  /** Number of unread messages while panel is closed + tab unfocused. */
  unreadCount: number
  /** Pre-formatted title to show when there are unreads, e.g. "(1) New message — Ahead Health". */
  alertTitle: string
}

const FAVICON_DOT_DATA_URL =
  // 32x32 transparent canvas with a single red dot in the upper-right.
  // Inline SVG → data URL keeps everything self-contained, no asset to
  // ship and no extra HTTP request.
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
       <rect width="32" height="32" fill="white"/>
       <text x="6" y="22" font-family="system-ui" font-size="16" fill="black">A</text>
       <circle cx="24" cy="8" r="6" fill="#ef4444"/>
     </svg>`.replace(/\s+/g, " ")
  )

/**
 * When the chat panel is closed (so the inline launcher dot is doing
 * the work for foregrounded users) AND the user has switched away to
 * another tab, mutate the document title and favicon so a new message
 * shows up in the browser tab strip — Slack / Gmail / GitHub style.
 *
 * Reverts as soon as the tab regains focus or unreadCount drops to 0.
 */
export function useTabAttention({
  unreadCount,
  alertTitle,
}: UseTabAttentionArgs): void {
  useEffect(() => {
    if (typeof document === "undefined") return

    const originalTitle = document.title
    const originalIcon =
      document
        .querySelector<HTMLLinkElement>('link[rel~="icon"]')
        ?.getAttribute("href") ?? null

    let active = false

    function applyAlert(): void {
      if (active) return
      active = true
      document.title = alertTitle
      const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
      if (link) link.setAttribute("href", FAVICON_DOT_DATA_URL)
    }

    function clearAlert(): void {
      if (!active) return
      active = false
      document.title = originalTitle
      const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
      if (link && originalIcon) link.setAttribute("href", originalIcon)
    }

    function update(): void {
      const tabHidden = document.visibilityState === "hidden"
      if (unreadCount > 0 && tabHidden) {
        applyAlert()
      } else {
        clearAlert()
      }
    }

    document.addEventListener("visibilitychange", update)
    update()

    return () => {
      document.removeEventListener("visibilitychange", update)
      clearAlert()
    }
  }, [unreadCount, alertTitle])
}
